/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect } from 'react';
import { updateTakeProfitApi } from '../api/api';

const UpdateTakeProfitModal = ({ isOpen, onClose, trade, onSuccessfulTpUpdate }) => {
  const [takeProfit, setTakeProfit] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isOpen && trade) {
      setTakeProfit(trade.profit_target ? String(trade.profit_target) : ''); // Pre-fill with current TP
      setError(null);
      setSuccessMessage('');
    } else if (!isOpen) {
      setTakeProfit('');
      setError(null);
      setSuccessMessage('');
    }
  }, [isOpen, trade]);

  if (!isOpen || !trade) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');

    if (!takeProfit || isNaN(parseFloat(takeProfit))) {
      setError({ detail: { take_profit: ['Take profit must be a valid number.'] } });
      setIsLoading(false);
      return;
    }
    // Backend expects a string for the decimal value
    const tpValue = String(parseFloat(takeProfit));

    try {
      const response = await updateTakeProfitApi(trade.trade_id, tpValue);
      setSuccessMessage(response.message || 'Take profit updated successfully!');
      // Optionally display more info from response.trade object
      if (onSuccessfulTpUpdate) {
        onSuccessfulTpUpdate();
      }
    } catch (apiError) {
      console.error("Update TP Error from API:", apiError);
      if (apiError && (apiError.detail || apiError.error)) {
        setError(apiError);
      } else if (typeof apiError === 'string') {
        setError({ detail: apiError });
      } else {
        setError({ detail: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldErrorMessage = (field) => {
    if (error && typeof error.detail === 'object' && error.detail[field] && Array.isArray(error.detail[field])) {
      return error.detail[field].join(' ');
    }
    return null;
  };

  const getGeneralErrorMessage = () => {
    if (error) {
      if (typeof error.detail === 'string') return error.detail;
      if (Array.isArray(error.detail) && error.detail.every(item => typeof item === 'string')) return error.detail.join(' ');
      if (typeof error.error === 'string') return error.error;
    }
    return null;
  };

  const generalError = getGeneralErrorMessage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-backgroundSurface p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-textPrimary">Update Take Profit for Trade {trade.position_id}</h2>
          <button onClick={onClose} className="text-textSecondary hover:text-textPrimary">&times;</button>
        </div>

        {successMessage && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{successMessage}</div>}
        {generalError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{generalError}</div>}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="takeProfit" className="block text-sm font-medium text-textSecondary mb-1">New Take Profit Price</label>
            <input
              type="number"
              step="any" // Or a more specific step like "0.00001" for forex
              id="takeProfit"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
              className="w-full p-2 border border-borderDefault rounded-md bg-backgroundInput text-textPrimary"
              placeholder="e.g., 1.12800"
              required
            />
            {getFieldErrorMessage('take_profit') && <p className="text-xs text-destructive mt-1">{getFieldErrorMessage('take_profit')}</p>}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="btn btn-secondary px-4 py-2 rounded-md text-textPrimary hover:bg-secondary/80 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary px-4 py-2 rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update Take Profit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateTakeProfitModal;
